import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useUnifiedAuth } from '../../contexts/UnifiedAuthContext';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import Loading from '../../components/Loading';
import {
  buildLoginRedirectPath,
  cloneSharedStrategy,
  getCloneAgentTargetId,
} from '../AiBotBuilder/cloneSharedStrategy';
import './SharePage.css';

const SHARE_STATUS = {
  LOADING: 'loading',
  VERIFYING: 'verifying',
  SUCCESS: 'success',
  ERROR: 'error',
  INVALID_TOKEN: 'invalid_token',
  NOT_FOUND: 'not_found',
};

export default function SharePage() {
  const { aiStrategyId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { isAuthenticated, isLoading: authLoading, getApiToken } = useUnifiedAuth();

  const [status, setStatus] = useState(SHARE_STATUS.LOADING);
  const [strategy, setStrategy] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareToken = searchParams.get('share');
  const isPublicShare = searchParams.get('public') === 'true';
  const shareType = shareToken ? 'private' : (isPublicShare ? 'public' : 'unknown');

  const verifyPrivateShare = useCallback(async () => {
    if (!shareToken || !aiStrategyId) return null;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/strategies/verify-share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ shareToken }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[SharePage] Token verification failed:', response.status, errorData);
        return { error: true, message: errorData.message || 'Invalid or expired share link' };
      }

      const data = await response.json();
      return { error: false, strategy: data.strategy };
    } catch (error) {
      console.error('[SharePage] Token verification exception:', error);
      return { error: true, message: 'Failed to verify share link. Please try again.' };
    }
  }, [shareToken, aiStrategyId]);

  const fetchPublicStrategy = useCallback(async () => {
    if (!aiStrategyId) return null;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/strategies/public/${aiStrategyId}`);

      if (!response.ok) {
        console.error('[SharePage] Public strategy fetch failed:', response.status);
        return { error: true, message: 'Strategy not found or not public' };
      }

      const data = await response.json();
      return { error: false, strategy: data };
    } catch (error) {
      console.error('[SharePage] Public strategy fetch exception:', error);
      return { error: true, message: 'Failed to load strategy. Please try again.' };
    }
  }, [aiStrategyId]);

  useEffect(() => {
    async function loadStrategy() {
      if (!aiStrategyId) {
        setStatus(SHARE_STATUS.ERROR);
        setErrorMessage('Invalid share link - missing strategy ID');
        return;
      }

      if (!shareToken && !isPublicShare) {
        setStatus(SHARE_STATUS.ERROR);
        setErrorMessage('Invalid share link - missing share token or public flag');
        return;
      }

      setStatus(SHARE_STATUS.VERIFYING);

      let result;
      if (shareToken) {
        result = await verifyPrivateShare();
      } else if (isPublicShare) {
        result = await fetchPublicStrategy();
      }

      if (!result) {
        setStatus(SHARE_STATUS.ERROR);
        setErrorMessage('Failed to load strategy');
        return;
      }

      if (result.error) {
        setStatus(shareToken ? SHARE_STATUS.INVALID_TOKEN : SHARE_STATUS.NOT_FOUND);
        setErrorMessage(result.message);
        return;
      }

      setStrategy(result.strategy);
      setStatus(SHARE_STATUS.SUCCESS);

      pushEvent({
        event: shareToken ? ANALYTICS_EVENTS.SHARED_STRATEGY_ACCESSED : ANALYTICS_EVENTS.PUBLIC_STRATEGY_ACCESSED,
        strategyId: result.strategy?.id,
        aiStrategyId: aiStrategyId,
        strategyName: result.strategy?.name,
        shareType: shareType,
      });
    }

    loadStrategy();
  }, [aiStrategyId, shareToken, isPublicShare, verifyPrivateShare, fetchPublicStrategy, shareType]);

  const handleCloneStrategy = async () => {
    if (!strategy?.id || !aiStrategyId) {
      console.warn('[SharePage] Cannot clone - missing strategy data');
      return;
    }

    setIsCloning(true);

    const fullPath = location.pathname + location.search;

    try {
      const { loginRequired, cloneResult } = await cloneSharedStrategy({
        strategy,
        aiStrategyId,
        shareToken,
        getApiToken,
      });

      if (loginRequired) {
        navigate(buildLoginRedirectPath(fullPath));
        setIsCloning(false);
        return;
      }

      navigate(`/ai-agent/${getCloneAgentTargetId(cloneResult)}`);
    } catch (error) {
      console.error('[SharePage] Clone exception:', error);
      setErrorMessage(error.message || 'Error cloning strategy. Please try again.');
      setIsCloning(false);
    }
  };

  const handleCopyUrl = async () => {
    const shareUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);

      pushEvent({
        event: ANALYTICS_EVENTS.SHARE_URL_COPIED,
        strategyId: strategy?.id,
        aiStrategyId: aiStrategyId,
        strategyName: strategy?.name,
      });
    } catch (err) {
      console.error('[SharePage] Failed to copy URL:', err);
    }
  };

  if (authLoading) {
    return (
      <div className="share-page" data-testid="share-page">
        <Loading message="Loading..." />
      </div>
    );
  }

  return (
    <div className="share-page" data-testid="share-page">
      <div className="share-page-container">
        {/* Loading State */}
        {(status === SHARE_STATUS.LOADING || status === SHARE_STATUS.VERIFYING) && (
          <div className="share-loading" data-testid="share-loading">
            <div className="loading-spinner"></div>
            <h2>Loading shared strategy...</h2>
            <p>Verifying your access...</p>
          </div>
        )}

        {/* Error State */}
        {(status === SHARE_STATUS.ERROR || status === SHARE_STATUS.INVALID_TOKEN || status === SHARE_STATUS.NOT_FOUND) && (
          <div className="share-error" data-testid="share-error">
            <div className="error-icon">⚠️</div>
            <h2>
              {status === SHARE_STATUS.INVALID_TOKEN && 'Invalid Share Link'}
              {status === SHARE_STATUS.NOT_FOUND && 'Strategy Not Found'}
              {status === SHARE_STATUS.ERROR && 'Error Loading Strategy'}
            </h2>
            <p className="error-message">{errorMessage}</p>
            <div className="error-actions">
              <button
                className="btn-secondary"
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
              <button
                className="btn-primary"
                onClick={() => navigate('/')}
              >
                Go Home
              </button>
            </div>
          </div>
        )}

        {/* Success State - Strategy Loaded */}
        {status === SHARE_STATUS.SUCCESS && strategy && (
          <div className="share-content" data-testid="share-content">
            {/* Badge */}
            <div className="share-badge">
              {shareType === 'private' ? 'Private Share' : 'Public Strategy'}
            </div>

            {/* Strategy Name */}
            <h1 className="strategy-name">{strategy.name || 'Shared Strategy'}</h1>

            {/* Description if available */}
            {strategy.description && (
              <p className="strategy-description">{strategy.description}</p>
            )}

            {/* Primary CTA - Clone Button */}
            <button
              className={`btn-clone-primary ${isCloning ? 'loading' : ''}`}
              onClick={handleCloneStrategy}
              disabled={isCloning}
              data-testid="clone-strategy-btn"
            >
              {isCloning ? (
                <>
                  <span className="btn-spinner"></span>
                  Cloning Strategy...
                </>
              ) : (
                'Clone This Strategy'
              )}
            </button>

            {/* Subtext under CTA */}
            <p className="cta-subtext">
              {isAuthenticated
                ? 'Add this strategy to your account and customize it with AI'
                : 'Sign up or log in to clone this strategy to your account'
              }
            </p>

            {/* Clone Error */}
            {errorMessage && (
              <div className="clone-error" role="alert">
                {errorMessage}
              </div>
            )}

            {/* Secondary action - Copy Link */}
            <button
              className={`btn-copy-link ${copied ? 'copied' : ''}`}
              onClick={handleCopyUrl}
              data-testid="copy-url-btn"
            >
              {copied ? 'Link Copied!' : 'Copy Share Link'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
